import type { CompTopicDetailBucket, OverviewCompetitorChartRow } from "@nexus/contracts";
import { scaleCompDaysAssets } from "../../lib/compTopicDetailMetrics.js";

export type CompetitorChartSortDirection = "asc" | "desc";

export const pctKey = (bucket: string) => `${bucket}Pct`;
export const metricKey = (bucket: string) => `${bucket}Metric`;

/**
 * Select the plotted right-axis value per the (frontend-only) Performance Metric pill.
 * Everything is precomputed server-side (the Retool transform); we only choose which
 * field to plot. `Days/Assets` uses the winsorized-normalized value (already clamped to
 * [-1, 1]) scaled to a percent for display; `null` ⇒ no dot (distinct from a real 0).
 */
export function metricOf(row: OverviewCompetitorChartRow, metric: string): number | null {
  if (metric === "Days") return row.Days;
  if (metric === "Assets") return row.Assets;
  return scaleCompDaysAssets(row.daysAssets);
}

// topic + per-bucket `${bucket}Pct` (left axis) and `${bucket}Metric` (right axis).
// null = the entity has no row for this topic (no data) — distinct from a real 0.
export interface TopicDatum {
  topic: string;
  /** Summed Assets across buckets present for this topic — default (no-column) sort key. */
  _sort: number;
  [key: string]: string | number | null;
}

function compareNullableNumber(
  av: string | number | null,
  bv: string | number | null,
  direction: CompetitorChartSortDirection,
): number {
  const an = typeof av === "number";
  const bn = typeof bv === "number";
  if (!an && !bn) return 0;
  if (!an) return 1;
  if (!bn) return -1;
  const dir = direction === "asc" ? 1 : -1;
  return (av - bv) * dir;
}

const BUCKETS: CompTopicDetailBucket[] = ["you", "mainCompetitor", "benchmark"];

function emptyTopic(topic: string): TopicDatum {
  const datum: TopicDatum = { topic, _sort: 0 };
  for (const bucket of BUCKETS) {
    datum[pctKey(bucket)] = null;
    datum[metricKey(bucket)] = null;
  }
  return datum;
}

/**
 * Pivot long `(topic × bucket)` rows into one datum per topic and order them.
 *
 * `sortBucket === null` keeps today's default: summed Assets descending.
 * A selected bucket orders by that series' Asset % (the bar column); topics with
 * no row for the bucket sort last in either direction.
 */
export function buildCompetitorChartData(
  chartRows: OverviewCompetitorChartRow[],
  performanceMetric: string,
  sortBucket: CompTopicDetailBucket | null = null,
  sortDirection: CompetitorChartSortDirection = "desc",
): TopicDatum[] {
  const byTopic = new Map<string, TopicDatum>();
  for (const r of chartRows) {
    const datum = byTopic.get(r.Topic_Title) ?? emptyTopic(r.Topic_Title);
    datum[pctKey(r.bucket)] = r.assetPct == null ? null : r.assetPct * 100;
    datum[metricKey(r.bucket)] = metricOf(r, performanceMetric);
    datum._sort += r.Assets;
    byTopic.set(r.Topic_Title, datum);
  }

  const data = [...byTopic.values()];
  if (sortBucket == null) {
    data.sort((a, b) => b._sort - a._sort);
    return data;
  }

  const key = pctKey(sortBucket);
  data.sort((a, b) => compareNullableNumber(a[key], b[key], sortDirection));
  return data;
}
